import React, { useCallback, useEffect, useState } from "react";
import {
  HelpCircle,
  Loader2,
  Plus,
  Pencil,
  Trash2,
} from "lucide-react";
import baseApi from "utils/axios-instance";
import { Mixpanel } from "utils/Analytics";
import { toast } from "sonner";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Toaster } from "components/ui/sonner";

interface CustomVerificationQuestion {
  id: string;
  team_id: string;
  user_id: string;
  question_text: string;
  creation_ts: string;
  last_updated_ts: string;
}

function VerificationsPage() {
  const [teamId, setTeamId] = useState<string | null>(null);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [customVerificationQuestions, setCustomVerificationQuestions] = useState<
    CustomVerificationQuestion[]
  >([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [showAddQuestionDialog, setShowAddQuestionDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] =
    useState<CustomVerificationQuestion | null>(null);
  const [questionText, setQuestionText] = useState("");
  const [savingQuestion, setSavingQuestion] = useState(false);
  const [deletingQuestionId, setDeletingQuestionId] = useState<string | null>(
    null
  );
  const [showDeleteQuestionDialog, setShowDeleteQuestionDialog] = useState(false);
  const [questionToDelete, setQuestionToDelete] =
    useState<CustomVerificationQuestion | null>(null);

  const fetchTeamId = useCallback(async () => {
    setLoadingTeam(true);
    try {
      const response = await baseApi.get("/dashboard/current-user", {
        withCredentials: true,
      });
      setTeamId(response.data.team_id || null);
    } catch (error) {
      console.error("Error fetching team info:", error);
      toast.error("Failed to load verification settings.");
    } finally {
      setLoadingTeam(false);
    }
  }, []);

  const fetchCustomVerificationQuestions = useCallback(async () => {
    if (!teamId) return;
    setLoadingQuestions(true);
    try {
      const response = await baseApi.get(
        "/dashboard/custom_verification_questions",
        { withCredentials: true }
      );
      setCustomVerificationQuestions(response.data || []);
    } catch (error: any) {
      console.error("Error fetching custom verification questions:", error);
      toast.error("Failed to load custom verification questions.", {
        description:
          error.response?.data?.detail || "An unexpected error occurred.",
      });
    } finally {
      setLoadingQuestions(false);
    }
  }, [teamId]);

  useEffect(() => {
    fetchTeamId();
  }, [fetchTeamId]);

  useEffect(() => {
    if (teamId) {
      fetchCustomVerificationQuestions();
    }
  }, [teamId, fetchCustomVerificationQuestions]);

  const handleSaveQuestion = async () => {
    if (!questionText.trim()) {
      toast.error("Please enter a question.");
      return;
    }

    if (questionText.trim().length < 3) {
      toast.error("Question must be at least 3 characters long.");
      return;
    }

    setSavingQuestion(true);
    try {
      if (editingQuestion) {
        await baseApi.put(
          `/dashboard/custom_verification_questions/${editingQuestion.id}`,
          { question_text: questionText.trim() },
          { withCredentials: true }
        );
        toast.success("Question updated successfully!");
        Mixpanel.track("Custom Verification Question Updated");
      } else {
        await baseApi.post(
          "/dashboard/custom_verification_questions",
          { question_text: questionText.trim() },
          { withCredentials: true }
        );
        toast.success("Question added successfully!");
        Mixpanel.track("Custom Verification Question Created");
      }
      setShowAddQuestionDialog(false);
      setEditingQuestion(null);
      setQuestionText("");
      fetchCustomVerificationQuestions();
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "An unexpected error occurred.";
      toast.error(message, {
        description: editingQuestion
          ? "Failed to update question."
          : "Failed to add question.",
      });
      console.error("Save question error:", error);
    } finally {
      setSavingQuestion(false);
    }
  };

  const handleEditQuestion = (question: CustomVerificationQuestion) => {
    setEditingQuestion(question);
    setQuestionText(question.question_text);
    setShowAddQuestionDialog(true);
  };

  const handleDeleteQuestionClick = (question: CustomVerificationQuestion) => {
    setQuestionToDelete(question);
    setShowDeleteQuestionDialog(true);
  };

  const handleDeleteQuestion = async () => {
    if (!questionToDelete) return;

    setDeletingQuestionId(questionToDelete.id);
    try {
      await baseApi.delete(
        `/dashboard/custom_verification_questions/${questionToDelete.id}`,
        { withCredentials: true }
      );
      toast.success("Question deleted successfully!");
      Mixpanel.track("Custom Verification Question Deleted");
      fetchCustomVerificationQuestions();
      setShowDeleteQuestionDialog(false);
      setQuestionToDelete(null);
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "An unexpected error occurred.";
      toast.error(message, {
        description: "Failed to delete question.",
      });
      console.error("Delete question error:", error);
    } finally {
      setDeletingQuestionId(null);
    }
  };

  const handleAddQuestion = () => {
    setEditingQuestion(null);
    setQuestionText("");
    setShowAddQuestionDialog(true);
  };

  return (
    <div className="w-full">
      <Toaster richColors position="top-center" />
      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-[#005151] rounded-xl">
                <HelpCircle className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Verifications
                </h1>
                <p className="text-gray-600 mt-1">
                  Add custom questions to verify in call transcripts
                </p>
              </div>
            </div>
            {teamId && (
              <Button
                onClick={handleAddQuestion}
                className="bg-[#005151] hover:bg-[#004040] text-white"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Question
              </Button>
            )}
          </div>
        </div>

        {loadingTeam ? (
          <Card className="border border-gray-200 shadow-sm">
            <CardContent className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-[#005151] mr-2" />
              <span className="text-gray-600">Loading verifications...</span>
            </CardContent>
          </Card>
        ) : !teamId ? (
          <Card className="border border-gray-200 shadow-sm">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <p className="text-gray-600">
                Verification questions are unavailable without a team account.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border border-gray-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold text-gray-900">
                Transcript Verification Questions
              </CardTitle>
              <CardDescription className="text-gray-600">
                These questions will be verified for all calls in your team
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingQuestions ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-[#005151] mr-2" />
                  <span className="text-gray-600">Loading questions...</span>
                </div>
              ) : customVerificationQuestions.length === 0 ? (
                <div className="text-center py-12">
                  <div className="p-4 bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <HelpCircle className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No custom questions yet
                  </h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    Add custom questions that will be checked during transcript
                    verification. These questions will be verified for all calls
                    in your team.
                  </p>
                  <Button
                    variant="outline"
                    className="border-[#005151] text-[#005151] hover:bg-[#005151] hover:text-white"
                    onClick={handleAddQuestion}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Your First Question
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {customVerificationQuestions.map((question) => (
                    <div
                      key={question.id}
                      className="flex items-start justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex-1">
                        <p className="text-gray-900 font-medium">
                          {question.question_text}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditQuestion(question)}
                          className="text-[#005151] hover:text-[#004040] hover:bg-[#005151]/10"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteQuestionClick(question)}
                          disabled={deletingQuestionId === question.id}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          {deletingQuestionId === question.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={showAddQuestionDialog} onOpenChange={setShowAddQuestionDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingQuestion
                ? "Edit Question"
                : "Add Custom Verification Question"}
            </DialogTitle>
            <DialogDescription>
              {editingQuestion
                ? "Update the question text below."
                : "Enter a question that will be verified in call transcripts. This question will be checked for all calls in your team."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label
                htmlFor="question-text"
                className="text-sm font-medium text-gray-700"
              >
                Question Text
              </label>
              <Input
                id="question-text"
                type="text"
                placeholder="e.g., Did we confirm the patient's date of birth?"
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !savingQuestion && questionText.trim()) {
                    handleSaveQuestion();
                  }
                }}
                className="h-12"
                disabled={savingQuestion}
              />
              <p className="text-xs text-gray-500">
                This question will be checked during transcript verification to
                ensure it was asked and answered.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddQuestionDialog(false);
                setEditingQuestion(null);
                setQuestionText("");
              }}
              disabled={savingQuestion}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveQuestion}
              disabled={savingQuestion || !questionText.trim()}
              className="bg-[#005151] hover:bg-[#004040] text-white"
            >
              {savingQuestion ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {editingQuestion ? "Updating..." : "Adding..."}
                </>
              ) : editingQuestion ? (
                "Update Question"
              ) : (
                "Add Question"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showDeleteQuestionDialog}
        onOpenChange={setShowDeleteQuestionDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Verification Question</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "
              {questionToDelete?.question_text || "this question"}"? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowDeleteQuestionDialog(false);
                setQuestionToDelete(null);
              }}
              disabled={!!deletingQuestionId}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteQuestion}
              disabled={!!deletingQuestionId}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deletingQuestionId ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default VerificationsPage;
